import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useFuncionarios } from '@/context/FuncionariosContext';
import { useSiadap } from '@/context/SiadapContext';
import { useAppSettings } from '@/context/AppSettingsContext';
import { useTheme } from '@/context/ThemeContext';
import { useActiveMunicipio } from '@/context/ActiveMunicipioContext';
import {
  calcularProgressao,
  calcularPontosCiclo,
  calcularLimiarFuncionario,
  aplicaRegraEspecial18Anos,
  PONTOS_REGRA_18_ANOS,
  type AvaliacaoSiadap,
  type PontosEscalasConfig,
} from '@/utils/progressaoUtils';
import { useMemo, useState, useRef, useEffect } from 'react';
import { useMediaQuery } from '@/hooks/useMediaQuery';

const DEFAULT_NAV_ICONS: Record<string, string> = {
  '/progressoes':              'ri-bar-chart-line',
  '/funcionarios':             'ri-team-line',
  '/siadap':                   'ri-award-line',
  '/historico':                'ri-history-line',
  '/resumo':                   'ri-pie-chart-2-line',
  '/relatorios':               'ri-file-chart-2-line',
  '/formacao':                 'ri-dashboard-line',
  '/formacao/acoes':           'ri-book-open-line',
  '/formacao/participantes':   'ri-user-star-line',
  '/formacao/funcionarios':    'ri-team-line',
  '/formacao/plano':           'ri-clipboard-line',
  '/formacao/documentos':      'ri-folder-open-line',
  '/formacao/relatorios':      'ri-pie-chart-2-line',
  '/formacao/calendario':      'ri-calendar-event-line',
  '/formacao/entidades':       'ri-building-2-line',
  '/formacao/alertas':         'ri-notification-3-line',
  '/formacao/requisitos':      'ri-scales-3-line',
  '/formacao/avaliacoes':      'ri-star-line',
  '/formacao/formadores':      'ri-user-voice-line',
  '/formacao/notificacoes':    'ri-mail-send-line',
  '/formacao/colaboradores':   'ri-user-star-line',
  '/formacao/indicadores':     'ri-bar-chart-grouped-line',
};

const DEFAULT_FORMACAO_NAV_ICONS: Record<string, string> = {
  '/formacao':                 'ri-dashboard-line',
  '/formacao/acoes':           'ri-book-open-line',
  '/formacao/participantes':   'ri-user-star-line',
  '/formacao/funcionarios':    'ri-team-line',
  '/formacao/plano':           'ri-clipboard-line',
  '/formacao/documentos':      'ri-folder-open-line',
  '/formacao/relatorios':      'ri-pie-chart-2-line',
  '/formacao/calendario':      'ri-calendar-event-line',
  '/formacao/entidades':       'ri-building-2-line',
  '/formacao/alertas':         'ri-notification-3-line',
  '/formacao/requisitos':      'ri-scales-3-line',
  '/formacao/avaliacoes':      'ri-star-line',
  '/formacao/formadores':      'ri-user-voice-line',
  '/formacao/notificacoes':    'ri-mail-send-line',
  '/formacao/indicadores':     'ri-bar-chart-grouped-line',
};

// Grupo SIADAP / Progressões
const siadapNavItems = [
  { to: '/funcionarios',  label: 'Funcionários', end: false },
  { to: '/siadap',        label: 'SIADAP',       end: false },
  { to: '/historico',     label: 'Histórico',    end: false },
  { to: '/resumo',        label: 'Resumo',       end: false },
  { to: '/progressoes',   label: 'Progressões',  end: false },
  { to: '/relatorios',    label: 'Relatórios',   end: false },
];

// Emails com acesso restrito apenas à Gestão da Formação
const FORMACAO_ONLY_EMAILS = ['vaz.sofia@gmail.com'];

// ── Grupos de navegação da Formação ──
interface NavGroup {
  key: string;
  label: string;
  icon: string;
  items: { to: string; label: string; end: boolean }[];
}

const formacaoNavGroups: NavGroup[] = [
  {
    key: 'principal',
    label: 'Principal',
    icon: 'ri-apps-line',
    items: [
      { to: '/formacao',               label: 'Resumo Executivo',     end: true  },
      { to: '/formacao/funcionarios',  label: 'Funcionários',         end: false },
      { to: '/formacao/colaboradores', label: 'Colaboradores',        end: false },
      { to: '/formacao/acoes',         label: 'Ações de Formação',    end: false },
      { to: '/formacao/participantes', label: 'Participantes',        end: false },
    ],
  },
  {
    key: 'planeamento',
    label: 'Planeamento',
    icon: 'ri-calendar-check-line',
    items: [
      { to: '/formacao/plano',      label: 'Plano de Formação',    end: false },
      { to: '/formacao/documentos', label: 'Documentos',           end: false },
      { to: '/formacao/calendario', label: 'Calendário',           end: false },
      { to: '/formacao/entidades',  label: 'Entidades Formadoras', end: false },
    ],
  },
  {
    key: 'monitorizacao',
    label: 'Monitorização',
    icon: 'ri-eye-line',
    items: [
      { to: '/formacao/relatorios',   label: 'Relatórios',           end: false },
      { to: '/formacao/alertas',      label: 'Alertas de Formação',  end: false },
      { to: '/formacao/requisitos',   label: 'Requisitos Legais',    end: false },
      { to: '/formacao/avaliacoes',  label: 'Avaliações',           end: false },
      { to: '/formacao/indicadores', label: 'Indicadores Qualidade', end: false },
      { to: '/formacao/formadores',  label: 'Formadores Internos',  end: false },
      { to: '/formacao/notificacoes', label: 'Notificações Email',   end: false },
    ],
  },
];

// Flat list para compatibilidade
const formacaoNavItems = formacaoNavGroups.flatMap((g) => g.items);

export default function Sidebar() {
  const { profile, signOut } = useAuth();
  const navigate = useNavigate();
  const { funcionarios, saving } = useFuncionarios();
  const { registosSiadap, registosHistorico, pontosDescontadosPorFuncionario } = useSiadap();
  const { settings } = useAppSettings();
  const { theme } = useTheme();
  const { activeMunicipio, municipios, setActiveMunicipio, isSwitching } = useActiveMunicipio();
  const [collapsed, setCollapsed] = useState(false);
  const [munDropOpen, setMunDropOpen] = useState(false);
  const munDropRef = useRef<HTMLDivElement>(null);

  const isMobileHeight = useMediaQuery('(max-height: 750px)');
  const isSmallScreen = useMediaQuery('(max-width: 1024px)');
  const useAccordion = isMobileHeight || isSmallScreen;

  // Estado de grupos expandidos (para accordion)
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() => {
    // Por defeito expandir o primeiro grupo
    return { principal: true };
  });

  const toggleGroup = (key: string) => {
    setExpandedGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (munDropRef.current && !munDropRef.current.contains(e.target as Node)) {
        setMunDropOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const pontosMap = useMemo<Partial<Record<AvaliacaoSiadap, number>>>(() => ({
    Excelente:   settings.pontos_excelente,
    'Muito Bom': settings.pontos_muito_bom,
    Bom:         settings.pontos_bom,
    Regular:     settings.pontos_regular,
    Inadequado:  settings.pontos_inadequado,
  }), [settings]);

  const escalas = useMemo<PontosEscalasConfig>(() => ({
    pos2025: {
      Excelente:   settings.pontos_excelente,
      'Muito Bom': settings.pontos_muito_bom,
      Bom:         settings.pontos_bom,
      Regular:     settings.pontos_regular,
      Inadequado:  settings.pontos_inadequado,
    },
    p2023_2024: {
      Excelente:   settings.pontos_excelente_2023,
      'Muito Bom': settings.pontos_muito_bom_2023,
      Bom:         settings.pontos_bom_2023,
      Regular:     settings.pontos_regular_2023,
      Inadequado:  settings.pontos_inadequado_2023,
    },
    pre2023: {
      Excelente:   settings.pontos_excelente_pre2023,
      Relevante:   settings.pontos_relevante_pre2023,
      Adequado:    settings.pontos_adequado_pre2023,
      Inadequado:  settings.pontos_inadequado_pre2023,
      'Muito Bom': settings.pontos_relevante_pre2023,
      Bom:         settings.pontos_adequado_pre2023,
      Regular:     settings.pontos_adequado_pre2023,
    },
  }), [settings]);

  const elegiveisCont = useMemo(() => {
    const limiarPos2025 = settings.pontos_progressao        || 8;
    const limiarPre2023 = settings.pontos_progressao_pre2025 || 10;
    const limiar2023_24 = settings.pontos_progressao_2023    || 8;
    return funcionarios.filter((f) => {
      const sd = registosSiadap.filter((r) => r.funcionarioId === f.id);
      const hd = registosHistorico.filter((r) => r.funcionarioId === f.id);
      const desc = pontosDescontadosPorFuncionario[f.id] ?? 0;
      const isPrepav = f.excecaoPrepav === true;
      const temReg = sd.length > 0 || hd.length > 0;
      // Pontos manuais são SEMPRE somados
      const manuais = f.pontosAtuais ?? 0;
      const pontos = calcularPontosCiclo(sd, hd, desc, manuais, pontosMap, undefined, escalas);
      const { limiar } = calcularLimiarFuncionario(sd, hd, limiarPos2025, limiarPre2023, limiar2023_24, escalas);
      const jaProgrediu = (pontosDescontadosPorFuncionario[f.id] ?? 0) > 0;
      const regraEsp = aplicaRegraEspecial18Anos(f.dataEntradaFuncaoPublica, pontos, jaProgrediu, 2024, limiar);
      const limiarEf = regraEsp ? PONTOS_REGRA_18_ANOS : limiar;
      return calcularProgressao(pontos, f.nivelAtual, limiarEf).status === 'pronto';
    }).length;
  }, [funcionarios, registosSiadap, registosHistorico, pontosMap, escalas, pontosDescontadosPorFuncionario, settings]);

  // Se o módulo ativo é Gestão da Formação, forçar sidebar clara (branca)
  const isFormacaoActive =
    activeMunicipio?.sigla === 'GF' ||
    activeMunicipio?.nome === 'Gestão da Formação' ||
    FORMACAO_ONLY_EMAILS.includes(profile?.email ?? '');

  // ── Configurações dinâmicas baseadas no módulo ativo ──
  const isLight = isFormacaoActive
    ? (theme.formacaoSidebarStyle ?? 'light') === 'light'
    : theme.sidebarStyle === 'light';

  const effectiveSidebarBg = isFormacaoActive
    ? (theme.formacaoSidebarBg ?? '#ffffff')
    : theme.sidebarBg;

  const effectiveSidebarStyle = isFormacaoActive
    ? (theme.formacaoSidebarStyle ?? 'light')
    : theme.sidebarStyle;

  const effectiveAccent = isFormacaoActive
    ? (theme.formacaoAccentColor ?? '#16a34a')
    : theme.sidebarAccent;

  const effectiveNavTextColor = isFormacaoActive
    ? (theme.formacaoNavTextColor ?? 'auto')
    : (theme.navTextColor ?? 'auto');

  const navBoldItems = isFormacaoActive
    ? (theme.formacaoNavBoldItems ?? ['/formacao'])
    : (theme.navBoldItems ?? ['/']);

  const navIcons = isFormacaoActive
    ? (theme.formacaoNavIcons ?? {})
    : (theme.navIcons ?? {});

  const navTextColor = effectiveNavTextColor;

  // Cor base dos itens de nav
  const baseColor = navTextColor === 'black'
    ? 'rgba(0,0,0,0.50)'
    : navTextColor === 'white'
      ? 'rgba(255,255,255,0.55)'
      : isLight ? 'rgba(0,0,0,0.42)' : 'rgba(255,255,255,0.42)';

  const activeColor = navTextColor === 'black'
    ? '#18181b'
    : navTextColor === 'white'
      ? '#ffffff'
      : isLight ? '#18181b' : '#ffffff';

  const activeBg = navTextColor === 'black'
    ? 'rgba(0,0,0,0.07)'
    : isLight ? 'rgba(0,0,0,0.07)' : 'rgba(255,255,255,0.12)';

  const dividerColor = isLight ? 'rgba(0,0,0,0.07)' : 'rgba(255,255,255,0.07)';
  const borderColor = isLight ? 'rgba(0,0,0,0.07)' : 'rgba(255,255,255,0.06)';

  const sidebarWidth = collapsed ? '56px' : '210px';

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const isFormacaoModule = activeMunicipio?.sigla === 'GF' || activeMunicipio?.nome === 'Gestão da Formação';
  const isSiadapModule = !isFormacaoModule;
  const forceFormacao = FORMACAO_ONLY_EMAILS.includes(profile?.email ?? '');
  const showSiadap = !forceFormacao && isSiadapModule;
  const showFormacao = forceFormacao || isFormacaoModule;

  return (
    <aside
      style={{
        width: sidebarWidth,
        minWidth: sidebarWidth,
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        borderRight: `1px solid ${borderColor}`,
        backgroundColor: effectiveSidebarBg,
        backgroundImage: isFormacaoActive && theme.formacaoHeroImageUrl ? `url(${theme.formacaoHeroImageUrl})` : !isFormacaoActive && theme.heroImageUrl ? `url(${theme.heroImageUrl})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        overflowY: 'auto',
        overflowX: 'hidden',
        transition: 'width 0.22s ease, min-width 0.22s ease',
        position: 'relative',
      }}
    >
      {/* Overlay de cor sobre a imagem de fundo */}
      {(isFormacaoActive ? theme.formacaoHeroImageUrl : theme.heroImageUrl) && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 0,
          backgroundColor: effectiveSidebarBg,
          opacity: 1 - (isFormacaoActive ? (theme.formacaoHeroImageSize ?? 100) : (theme.heroImageSize ?? 100)) / 100,
          pointerEvents: 'none',
        }} />
      )}
      {/* Wrapper para conteúdo ficar acima do overlay */}
      <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {/* Header: logo + toggle */}
      <div style={{
        padding: collapsed ? '16px 0' : '18px 14px 14px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'space-between',
        gap: '8px',
        flexShrink: 0,
      }}>
        {/* Logo / Brand — só mostra quando expandido */}
        {!collapsed && (
          isFormacaoActive ? (
            /* Header dedicado ao módulo de Formação */ 
            <div style={{ display: 'flex', alignItems: 'center', gap: '9px', flex: 1, minWidth: 0 }}>
              {theme.formacaoLogoUrl ? (
                <img src={theme.formacaoLogoUrl} alt="Logo" style={{ height: `${theme.formacaoLogoSize ?? 26}px`, objectFit: 'contain', flexShrink: 0 }} />
              ) : (
                <div style={{
                  width: '28px', height: '28px', borderRadius: '8px', flexShrink: 0,
                  backgroundColor: `${effectiveAccent}15`,
                  border: `1px solid ${effectiveAccent}30`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <i className="ri-book-open-line" style={{ fontSize: '13px', color: effectiveAccent }} />
                </div>
              )}
              <div style={{ minWidth: 0 }}>
                <p style={{
                  fontSize: '12px', fontWeight: 700, lineHeight: 1.3, margin: 0,
                  color: isLight ? '#18181b' : '#ffffff',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {theme.formacaoNome ?? activeMunicipio?.nome ?? 'Gabinete de Formação'}
                </p>
                <p style={{ fontSize: '10px', color: effectiveAccent, fontWeight: 600, lineHeight: 1.3, margin: '1px 0 0', whiteSpace: 'nowrap' }}>
                  {theme.formacaoSubtitulo ?? 'Gestão da Formação'}
                </p>
              </div>
            </div>
          ) : theme.logoUrl ? (
            <img src={theme.logoUrl} alt="Logo" style={{ height: `${theme.logoSize ?? 26}px`, objectFit: 'contain', flex: 1, minWidth: 0 }} />
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '9px', flex: 1, minWidth: 0 }}>
              <div style={{
                width: '28px', height: '28px', borderRadius: '7px', flexShrink: 0,
                backgroundColor: `${effectiveAccent}22`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <span style={{ fontSize: '10px', fontWeight: 800, color: effectiveAccent, lineHeight: 1, letterSpacing: '-0.3px' }}>
                  SR
                </span>
              </div>
              <div style={{ minWidth: 0 }}>
                <p style={{
                  fontSize: '12px', fontWeight: 600, lineHeight: 1.3, margin: 0,
                  color: isLight ? '#18181b' : '#ffffff',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {profile?.municipio_nome || theme.nomeCamara || 'Câmara Municipal'}
                </p>
                <p style={{ fontSize: '10px', color: baseColor, lineHeight: 1.3, margin: '1px 0 0', whiteSpace: 'nowrap' }}>
                  {theme.subtituloSidebar || (settings.ciclo_siadap ? `SIADAP · Ciclo ${settings.ciclo_siadap}` : 'SIADAP para RH')}
                </p>
              </div>
            </div>
          )
        )}

        {/* Botão recolher/expandir */}
        <button
          onClick={() => setCollapsed((v) => !v)}
          title={collapsed ? 'Expandir sidebar' : 'Recolher sidebar'}
          style={{
            width: '26px', height: '26px', borderRadius: '7px', flexShrink: 0,
            border: 'none', background: 'transparent', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: baseColor, transition: 'background 0.15s, color 0.15s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = activeBg;
            e.currentTarget.style.color = activeColor;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = baseColor;
          }}
        >
          <i
            className={collapsed ? 'ri-menu-unfold-line' : 'ri-menu-fold-line'}
            style={{ fontSize: '14px' }}
          />
        </button>
      </div>

      {/* Divider */}
      <div style={{ height: '1px', backgroundColor: dividerColor, margin: collapsed ? '0 8px 10px' : '0 14px 10px', flexShrink: 0 }} />

      {/* Seletor de Módulo/Município — só mostra se houver mais de 1 */}
      {municipios.length > 1 && (
        <div
          ref={munDropRef}
          style={{ padding: collapsed ? '0 6px 8px' : '0 6px 8px', position: 'relative', flexShrink: 0 }}
        >
          <button
            onClick={() => setMunDropOpen((v) => !v)}
            title={collapsed ? (activeMunicipio?.nome ?? 'Módulo') : undefined}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: collapsed ? 0 : '8px',
              justifyContent: collapsed ? 'center' : 'space-between',
              padding: collapsed ? '8px 0' : '7px 10px',
              borderRadius: '8px',
              border: `1px solid ${isSwitching ? effectiveAccent + '60' : dividerColor}`,
              backgroundColor: isSwitching ? `${effectiveAccent}18` : `${effectiveAccent}10`,
              cursor: 'pointer',
              transition: 'background 0.2s, border-color 0.2s',
            }}
            onMouseEnter={(e) => { if (!isSwitching) e.currentTarget.style.backgroundColor = `${effectiveAccent}1a`; }}
            onMouseLeave={(e) => { if (!isSwitching) e.currentTarget.style.backgroundColor = `${effectiveAccent}10`; }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '7px', minWidth: 0 }}>
              <div style={{
                width: '20px', height: '20px', borderRadius: '5px', flexShrink: 0,
                backgroundColor: effectiveAccent,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {isSwitching
                  ? <i className="ri-loader-4-line" style={{ fontSize: '10px', color: '#fff', animation: 'spin 0.7s linear infinite' }} />
                  : <span style={{ fontSize: '9px', fontWeight: 700, color: '#fff', lineHeight: 1 }}>
                      {activeMunicipio?.sigla?.slice(0, 2) ?? '?'}
                    </span>
                }
              </div>
              {!collapsed && (
                <span style={{
                  fontSize: '12px', fontWeight: 600,
                  color: isSwitching ? effectiveAccent : (isLight ? '#18181b' : '#ffffff'),
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  flex: 1, minWidth: 0,
                  transition: 'color 0.2s',
                }}>
                  {isSwitching ? 'A carregar...' : (activeMunicipio?.nome ?? 'Selecionar módulo')}
                </span>
              )}
            </div>
            {!collapsed && (
              <i
                className={isSwitching ? 'ri-loader-4-line' : (munDropOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line')}
                style={{
                  fontSize: '13px',
                  color: isSwitching ? effectiveAccent : baseColor,
                  flexShrink: 0,
                  animation: isSwitching ? 'spin 0.7s linear infinite' : 'none',
                }}
              />
            )}
          </button>

          {/* Dropdown de municípios */}
          {munDropOpen && (
            <div style={{
              position: 'absolute',
              top: 'calc(100% - 2px)',
              left: '6px',
              right: '6px',
              backgroundColor: isLight ? '#ffffff' : '#1e1e2e',
              border: `1px solid ${dividerColor}`,
              borderRadius: '10px',
              overflow: 'hidden',
              zIndex: 100,
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            }}>
              {municipios.map((m) => {
                const isSelected = m.id === activeMunicipio?.id;
                return (
                  <button
                    key={m.id}
                    onClick={() => {
                      const isFormacao = m.sigla === 'GF' || m.nome === 'Gestão da Formação';
                      setActiveMunicipio(m);
                      setMunDropOpen(false);
                      navigate(isFormacao ? '/formacao' : '/funcionarios');
                    }}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '9px',
                      padding: '9px 12px',
                      border: 'none',
                      backgroundColor: isSelected ? `${theme.sidebarAccent}15` : 'transparent',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'background 0.1s',
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) e.currentTarget.style.backgroundColor = isLight ? '#f4f4f5' : 'rgba(255,255,255,0.06)';
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <div style={{
                      width: '22px', height: '22px', borderRadius: '6px', flexShrink: 0,
                      backgroundColor: isSelected ? theme.sidebarAccent : `${theme.sidebarAccent}30`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <span style={{ fontSize: '9px', fontWeight: 700, color: isSelected ? '#fff' : theme.sidebarAccent, lineHeight: 1 }}>
                        {m.sigla?.slice(0, 2) ?? '?'}
                      </span>
                    </div>
                    <span style={{
                      fontSize: '12px', fontWeight: isSelected ? 600 : 500,
                      color: isSelected ? (isLight ? '#18181b' : '#ffffff') : baseColor,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {m.nome}
                    </span>
                    {isSelected && (
                      <i className="ri-check-line" style={{ fontSize: '13px', color: effectiveAccent, marginLeft: 'auto', flexShrink: 0 }} />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Overlay de switching */}
      {isSwitching && (
        <div style={{
          position: 'absolute',
          inset: 0,
          zIndex: 10,
          pointerEvents: 'none',
          background: `linear-gradient(to bottom, transparent 0%, ${effectiveSidebarBg}cc 60%, ${effectiveSidebarBg} 100%)`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-end',
          paddingBottom: '32px',
          gap: '8px',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '8px 14px', borderRadius: '99px',
            backgroundColor: `${effectiveAccent}22`,
            border: `1px solid ${effectiveAccent}40`,
          }}>
            <i className="ri-loader-4-line" style={{
              fontSize: '13px', color: effectiveAccent,
              animation: 'spin 0.7s linear infinite',
            }} />
            {!collapsed && (
              <span style={{ fontSize: '11px', fontWeight: 600, color: effectiveAccent }}>
                A carregar dados...
              </span>
            )}
          </div>
        </div>
      )}

      {/* Nav items — SIADAP flat, Formação com grupos accordion */}
      <nav style={{ flex: 1, padding: '0 6px', display: 'flex', flexDirection: 'column', gap: '1px', opacity: isSwitching ? 0.4 : 1, transition: 'opacity 0.2s', overflowY: 'auto' }}>

        {/* Label do módulo ativo */}
        {!collapsed && (
          <div style={{ padding: '6px 4px 3px', marginTop: '2px' }}>
            <span style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: baseColor, opacity: 0.6 }}>
              {showFormacao ? 'Gestão da Formação' : 'SIADAP'}
            </span>
          </div>
        )}
        {collapsed && <div style={{ height: '8px' }} />}

        {/* ── SIADAP: lista flat ── */}
        {showSiadap && (
          siadapNavItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              title={collapsed ? item.label : undefined}
              style={({ isActive }) => ({
                display: 'flex',
                alignItems: 'center',
                gap: collapsed ? 0 : '9px',
                padding: collapsed ? '9px 0' : '8px 10px',
                justifyContent: collapsed ? 'center' : 'flex-start',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: navBoldItems.includes(item.to) ? 700 : 500,
                textDecoration: 'none',
                transition: 'background 0.13s, color 0.13s',
                cursor: 'pointer',
                color: isActive ? activeColor : baseColor,
                backgroundColor: isActive ? activeBg : 'transparent',
                position: 'relative',
              })}
            >
              {({ isActive }) => (
                <>
                  <i
                    className={navIcons[item.to] || DEFAULT_NAV_ICONS[item.to] || 'ri-circle-line'}
                    style={{ fontSize: '15px', flexShrink: 0, color: 'inherit' }}
                  />
                  {!collapsed && (
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.label}
                    </span>
                  )}
                  {item.to === '/progressoes' && elegiveisCont > 0 && (
                    <span style={{
                      minWidth: '17px', height: '17px', padding: '0 4px',
                      borderRadius: '99px', fontSize: '10px', fontWeight: 700,
                      backgroundColor: isActive ? effectiveAccent : `${effectiveAccent}cc`,
                      color: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      lineHeight: 1,
                      position: collapsed ? 'absolute' : 'static',
                      top: collapsed ? '4px' : undefined,
                      right: collapsed ? '4px' : undefined,
                    }}>
                      {elegiveisCont}
                    </span>
                  )}
                </>
              )}
            </NavLink>
          ))
        )}

        {/* ── Formação: grupos accordion ── */}
        {showFormacao && (
          useAccordion && !collapsed
            ? formacaoNavGroups.map((group) => {
                const isExpanded = expandedGroups[group.key] ?? false;
                return (
                  <div key={group.key} style={{ marginBottom: '2px' }}>
                    {/* Cabeçalho do grupo */}
                    <button
                      onClick={() => toggleGroup(group.key)}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '9px',
                        padding: '7px 10px',
                        borderRadius: '8px',
                        fontSize: '12px',
                        fontWeight: 600,
                        textDecoration: 'none',
                        cursor: 'pointer',
                        color: baseColor,
                        backgroundColor: isExpanded ? `${effectiveAccent}10` : 'transparent',
                        border: 'none',
                        transition: 'background 0.13s, color 0.13s',
                        position: 'relative',
                      }}
                      title={collapsed ? group.label : undefined}
                    >
                      <i
                        className={group.icon}
                        style={{ fontSize: '14px', flexShrink: 0, color: 'inherit', opacity: 0.7 }}
                      />
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left' }}>
                        {group.label}
                      </span>
                      <i
                        className={isExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}
                        style={{ fontSize: '14px', flexShrink: 0, color: 'inherit', opacity: 0.5 }}
                      />
                    </button>

                    {/* Items do grupo */}
                    {isExpanded && (
                      <div style={{ padding: '2px 0 2px 8px', display: 'flex', flexDirection: 'column', gap: '1px' }}>
                        {group.items.map((item) => (
                          <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.end}
                            style={({ isActive }) => ({
                              display: 'flex',
                              alignItems: 'center',
                              gap: '9px',
                              padding: '7px 10px',
                              borderRadius: '8px',
                              fontSize: '12px',
                              fontWeight: navBoldItems.includes(item.to) ? 700 : 500,
                              textDecoration: 'none',
                              transition: 'background 0.13s, color 0.13s',
                              cursor: 'pointer',
                              color: isActive ? activeColor : baseColor,
                              backgroundColor: isActive ? activeBg : 'transparent',
                            })}
                          >
                            {({ isActive }) => (
                              <>
                                <i
                                  className={navIcons[item.to] || DEFAULT_FORMACAO_NAV_ICONS[item.to] || 'ri-circle-line'}
                                  style={{ fontSize: '14px', flexShrink: 0, color: 'inherit' }}
                                />
                                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {item.label}
                                </span>
                              </>
                            )}
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            : (
              // Flat list para ecrãs grandes ou sidebar collapsed
              formacaoNavItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  title={collapsed ? item.label : undefined}
                  style={({ isActive }) => ({
                    display: 'flex',
                    alignItems: 'center',
                    gap: collapsed ? 0 : '9px',
                    padding: collapsed ? '9px 0' : '8px 10px',
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: navBoldItems.includes(item.to) ? 700 : 500,
                    textDecoration: 'none',
                    transition: 'background 0.13s, color 0.13s',
                    cursor: 'pointer',
                    color: isActive ? activeColor : baseColor,
                    backgroundColor: isActive ? activeBg : 'transparent',
                    position: 'relative',
                  })}
                >
                  {({ isActive }) => (
                    <>
                      <i
                        className={navIcons[item.to] || DEFAULT_FORMACAO_NAV_ICONS[item.to] || 'ri-circle-line'}
                        style={{ fontSize: '15px', flexShrink: 0, color: 'inherit' }}
                      />
                      {!collapsed && (
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.label}
                        </span>
                      )}
                    </>
                  )}
                </NavLink>
              ))
            )
        )}
      </nav>

      {/* Saving indicator */}
      {saving && (
        <div style={{
          margin: '0 6px 6px',
          padding: collapsed ? '8px 0' : '7px 10px',
          borderRadius: '8px',
          backgroundColor: `${effectiveAccent}15`,
          border: `1px solid ${effectiveAccent}20`,
          display: 'flex', alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          gap: '7px',
          flexShrink: 0,
        }}>
          <i className="ri-loader-4-line" style={{ fontSize: '12px', color: effectiveAccent }} />
          {!collapsed && <span style={{ fontSize: '11px', color: `${effectiveAccent}cc` }}>A guardar...</span>}
        </div>
      )}

      {/* Espaço final */}
      <div style={{ height: '8px', flexShrink: 0 }} />

      {/* ── User Menu ── */}
      <div style={{
        margin: '0 6px 8px',
        borderTop: `1px solid ${dividerColor}`,
        paddingTop: '8px',
        flexShrink: 0,
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: collapsed ? 0 : '9px',
          justifyContent: collapsed ? 'center' : 'flex-start',
          padding: collapsed ? '8px 0' : '8px 10px',
          borderRadius: '8px',
          cursor: 'default',
        }}>
          {/* Avatar */}
          <div style={{
            width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0,
            backgroundColor: `${effectiveAccent}22`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: `1px solid ${effectiveAccent}33`,
          }}>
            <span style={{ fontSize: '11px', fontWeight: 700, color: effectiveAccent, lineHeight: 1 }}>
              {profile?.full_name
                ? profile.full_name.split(' ').filter((_: string, i: number, arr: string[]) => i === 0 || i === arr.length - 1).map((n: string) => n[0]).join('').toUpperCase()
                : '?'}
            </span>
          </div>

          {/* Nome + role — só quando expandido */}
          {!collapsed && (
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{
                fontSize: '12px', fontWeight: 600, lineHeight: 1.3, margin: 0,
                color: isLight ? '#18181b' : '#ffffff',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {profile?.full_name?.split(' ')[0] ?? '—'}
              </p>
              <p style={{
                fontSize: '10px', color: baseColor, lineHeight: 1.3, margin: '1px 0 0',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {profile?.role === 'super_admin' ? 'Super Admin' : profile?.role === 'admin' ? 'Admin' : 'Visualizador'}
              </p>
            </div>
          )}

          {/* Botão sair */}
          {!collapsed && (
            <button
              onClick={handleSignOut}
              title="Terminar sessão"
              style={{
                width: '26px', height: '26px', borderRadius: '7px', flexShrink: 0,
                border: 'none', background: 'transparent', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: baseColor, transition: 'background 0.15s, color 0.15s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(239,68,68,0.12)';
                e.currentTarget.style.color = '#ef4444';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = baseColor;
              }}
            >
              <i className="ri-logout-box-r-line" style={{ fontSize: '14px' }} />
            </button>
          )}
        </div>

        {/* Quando collapsed: só botão de sair */}
        {collapsed && (
          <button
            onClick={handleSignOut}
            title="Terminar sessão"
            style={{
              width: '100%', padding: '8px 0', borderRadius: '8px',
              border: 'none', background: 'transparent', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: baseColor, transition: 'background 0.15s, color 0.15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(239,68,68,0.12)';
              e.currentTarget.style.color = '#ef4444';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = baseColor;
            }}
          >
            <i className="ri-logout-box-r-line" style={{ fontSize: '14px' }} />
          </button>
        )}
      </div>
      </div>{/* fim do wrapper zIndex:1 */}
    </aside>
  );
}
